import { useEffect, useRef, useState } from "react"
import Form, { useForm } from "../../gui/Form/Form"
import FormControl from "../../gui/Form/FormControl"
import Validators from "../../gui/Form/Validators"
import { useOverlay } from "../../gui/Overlay/OverlayProvider"
import { usePath } from "../../libs/path/PathProvider"
import AccountSelect from "../widgets/AccountSelect"
import Button from "../../gui/Button/Button"
import TextInput, { Formatters, ITextInput } from "../../gui/TextInput/TextInput"
import DatePicker, { IDatePicker } from "../../gui/Calendar/DatePicker"
import { IDropdown } from "../../gui/Dropdown/Dropdown"
import usePortfolio from "../../hooks/PortfolioProvider"
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider"
import { ETransactionType, TransFields } from "../../shared/ITransaction"
import PortUtils from "../../portlibs/PortUtils"
import { Transaction } from "../../models/Transaction"
import Utils from "../../libs/Utils"
import PortAccount from "../../models/PortAccount"
import { EAccountTypes } from "../../shared/IAccount"

export type TPaymentProps = {
    holdingId:string
}

export default function TransferDlg(props:TPaymentProps){
    const [busy,setBusy] = useState<boolean>(false)
    const refQty = useRef<ITextInput|null>(null)
    const refAccountFrom = useRef<IDropdown<number>|null>(null)
    const refAccountTo = useRef<IDropdown<number>|null>(null)
    const refDate = useRef<IDatePicker|null>(null)
    const {popOverlay} = useOverlay()
    const {valid} = useForm()
    const {portfolio} = usePortfolio()
    const {query} = usePath()
    const {account} = PortUtils.getFilter(query, 0)

    const holding = portfolio.holding(props.holdingId)!
    const ticker = holding.id
    const title = holding!.name + ` ${ticker}`
    const {addSnackbar} = useSnackbar()

    const onSave = async () => {
        if (busy) return
        else setBusy(true)

        const isPayment = portfolio.account(refAccountTo.current!.value!)?.type==EAccountTypes.credit
        const note = isPayment ? "Payment" : "Transfer"
        try {
            const trans = new Transaction(holding, {
                [TransFields.type] : ETransactionType.buy,
                [TransFields.ticker] : holding.id,
                [TransFields.account] : refAccountTo.current!.value!,
                [TransFields.quantity] : Number(refQty.current!.value!),
                [TransFields.date] : Utils.formatDate("yyyy-MM-dd", refDate.current!.value!),
                [TransFields.price] : 1,
                [TransFields.note] : note
            })
            await PortAccount.i.update(portfolio, trans, false)
    
            const cash = trans.clone(true)
            cash.accountId = refAccountFrom.current!.value!
            cash.quantity = -cash.quantity
            cash.note = portfolio.accountName(account) + " " + note
            await PortAccount.i.update(portfolio, cash, false)
            popOverlay(PortUtils.AddHoldingId)
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    }

    useEffect(() => {
        
    }, [])

    return <>
        <h2 className="title">Payment/Transfer</h2>
        <div className="content">
            <div className="grid gap-16">
                <FormControl validators={[Validators.Required("Account from")]}>
                    <AccountSelect ref={refAccountFrom} defaultValue={0} placeholder="Account from"/>
                </FormControl>
                <FormControl validators={[Validators.Required("Account to")]}>
                    <AccountSelect ref={refAccountTo} defaultValue={account} placeholder="Account to"/>
                </FormControl>
            </div>
            <div className="grid gap-16">
                <FormControl validators={[Validators.NonZeroNumber("Amount")]}>
                    <TextInput ref={refQty} placeholder="Amount" float={true} evaluator={Formatters.Evaluate}/>
                </FormControl>
                <FormControl validators={[Validators.Required("Date")]}>
                    <DatePicker ref={refDate} placeholder="Date" float={true} defaultValue={new Date()}/>
                </FormControl>
            </div>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(PortUtils.AddHoldingId)}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={!valid||busy}>Save</Button>
        </div>
    </>
}

export function useTransferDlg(){
    const {addOverlay,hasOverlay} = useOverlay()
    const {hash,setHash} = usePath()
    const {portfolio} = usePortfolio()

    const afterClosed = () => {
        const {holdingId} = PortUtils.parseHash(hash)
        setHash(PortUtils.createHash(holdingId))
    }
    const addTransfer = (props:TPaymentProps) => {
        if (hasOverlay(PortUtils.AddHoldingId) || !portfolio.holding(props.holdingId)) return
        addOverlay(PortUtils.AddHoldingId, {
            children:<Form><TransferDlg {...props}/></Form>,
            className:"dialog popup",
            afterClosed
        })
    }
    return {addTransfer}
}